import React, { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import BackToTop from "../components/BackToTop";
import PageHeader from "../components/PageHeader";
import { getAudioFile, getAudios } from "../utils/audioStorage";
import "../css/mediaPage.css";
import axios from "axios";
import headerBg from "../assets/backgrund-1.jpg";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000/api";

export default function MoaVoiceNotes() {
  const [items, setItems] = useState([]);
  const [playingItem, setPlayingItem] = useState(null);
  const [audioSrc, setAudioSrc] = useState(null);

  const getCanonicalKey = (item) => {
    const raw = item.sessionDate || item.uploadedAt || "";
    if (!raw) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
    const parsed = new Date(raw);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toISOString().slice(0, 10);
    }
    return raw;
  };

  useEffect(() => {
    const load = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/moa/audios`);
        const data = await response.json();
        const list = Array.isArray(data) ? data : [];
        
        list.sort((a, b) => {
          const keyA = getCanonicalKey(a);
          const keyB = getCanonicalKey(b);
          if (keyA === keyB) {
            return (Number(a.partNumber) || 0) - (Number(b.partNumber) || 0);
          }
          return keyA.localeCompare(keyB);
        });
        
        // Normalize all audio sources - backend now returns full URLs
        const processedList = list.map(item => {
            let src = item.audioSource;
            // Backend now ensures full URLs, but keep fallback for legacy data
            if (src && !src.startsWith('http')) {
                const baseUrl = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api').replace('/api', '');
                src = `${baseUrl}${src.startsWith('/') ? '' : '/'}${src}`;
            }
            return { ...item, audioSource: src };
        });
        
        setItems(processedList);
      } catch (err) {
        console.error("Failed to load MOA audios", err);
      }
    };

    load();
  }, []);

  const handlePlay = (item) => {
    setPlayingItem(item);
    
    // Determine the raw source
    let rawSrc = item.audioSource || "";
    
    if (rawSrc) {
        // Add timestamp to prevent caching issues
        const timestamp = Date.now();
        const audioUrlWithCache = rawSrc.includes('?') 
            ? `${rawSrc}&t=${timestamp}` 
            : `${rawSrc}?t=${timestamp}`;
        setAudioSrc(audioUrlWithCache);
    } else {
        setAudioSrc(null);
    }
  };

  const closeModal = () => {
    setPlayingItem(null);
    setAudioSrc(null);
  };

  return (
    <div className="media-page-body">
      <Navbar />
      <PageHeader title="MOA's Voice Note" path="/history/voice-notes" name="MOA's Voice Note" bgImage={headerBg} />

      <div className="media-hero" style={{ 
          backgroundImage: `linear-gradient(rgba(0, 33, 71, 0.9), rgba(10, 10, 10, 0.9)), url("${headerBg}")`,
          backgroundAttachment: 'fixed',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover'
      }}>
        <div className="container media-hero-content">
          <h1>Voice of MOA</h1>
          <p>
            Listen to the declarations, guidance and reflections of Modupeola Onitiri-Abiola arranged
            in a simple sequence of parts.
          </p>
        </div>
      </div>

      <div className="container-xxl py-5">
        <div className="container">
          <div className="section-title text-center mx-auto mb-5" style={{ maxWidth: "600px" }}>
            <h2 className="display-6">Audio Parts</h2>
            <div className="bg-primary mx-auto" style={{ width: "80px", height: "4px", borderRadius: "2px" }} />
          </div>

          {items.length === 0 && (
            <p style={{ textAlign: "center", color: "#64748b" }}>
              No voice notes have been uploaded yet. Use the Admin dashboard to add MOA&apos;s voice notes.
            </p>
          )}

          {items.length > 0 && (() => {
            const map = {};
            items.forEach((item) => {
              const canonical = getCanonicalKey(item) || "Other";
              const label = item.uploadedAt || canonical;
              if (!map[canonical]) {
                map[canonical] = { key: canonical, label, items: [] };
              }
              map[canonical].items.push(item);
            });
            const groups = Object.values(map);

            return groups.map((group) => (
              <div key={group.key} style={{ marginBottom: "2.5rem" }}>
                <h4 style={{ marginBottom: "1.25rem", color: "#0f172a" }}>
                  Uploaded: {group.label}
                </h4>
                <div className="row moa-audio-list">
                  {group.items.map((item, index) => (
                    <div className="col-md-6" key={item.id || `${group.key}-${index}`}>
                      <div className="audio-list-item moa-audio-item">
                        <div className="audio-index">Part {item.partNumber || index + 1}</div>
                        <div className="audio-icon-small">
                          <i className="fa fa-microphone" />
                        </div>
                        <div className="audio-info">
                          <h5 className="audio-title">
                            {item.title || `Part ${item.partNumber || index + 1}`}
                          </h5>
                          {item.desc && (
                            <p className="audio-artist" style={{ marginBottom: 0 }}>
                              {item.desc}
                            </p>
                          )}
                          {item.uploadedAt && (
                            <p
                              style={{
                                marginBottom: 0,
                                marginTop: 4,
                                fontSize: "0.8rem",
                                color: "#94a3b8",
                              }}
                            >
                              Uploaded: {item.uploadedAt}
                            </p>
                          )}
                        </div>
                        <button className="btn-play-mini" onClick={() => handlePlay(item)}>
                          <i className="fa fa-play" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ));
          })()}
        </div>
      </div>

      {playingItem && audioSrc && (
        <div className="video-modal-overlay" onClick={closeModal}>
          <div
            className="video-modal-content audio-modal"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "500px", textAlign: "center", padding: "2rem" }}
          >
            <button className="modal-close-btn" onClick={closeModal}>
              &times;
            </button>
            <div style={{ marginBottom: "1.5rem" }}>
              <div
                style={{
                  width: "120px",
                  height: "120px",
                  background: "#f1f5f9",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 1rem",
                  fontSize: "3rem",
                  color: "#64748b",
                }}
              >
                <i className="fa fa-microphone" />
              </div>
              <div className="moa-modal-part-pill">
                Part {playingItem.partNumber || ""}
              </div>
            </div>
            <audio controls autoPlay style={{ width: "100%" }}>
              <source src={audioSrc} type="audio/mpeg" />
              <source src={audioSrc} type="audio/mp4" />
              <source src={audioSrc} type="audio/x-m4a" />
              <source src={audioSrc} type="audio/wav" />
              <source src={audioSrc} type="video/quicktime" />
              Your browser does not support the audio element.
            </audio>
          </div>
        </div>
      )}

      <Footer />
      <BackToTop />
    </div>
  );
}
